/**
 * After a run fails: read the tail of its log and say WHY, mapped to the
 * Check setup item (and fix) that covers it. Pure.
 *
 *   exit 127 / "command not found" for a node bin   → Node packages not installed
 *   "command not found" for a toolchain             → toolchain missing
 *   mix "Unchecked dependencies" / run "mix deps.get" → Elixir deps not fetched
 *   "Cannot find module" / ERR_MODULE_NOT_FOUND      → Node packages
 *   ModuleNotFoundError                             → Python packages
 *   could not connect / econnrefused :5432          → database not reachable
 *   database "x" does not exist / ecto.create hint  → database not set up
 *   a missing environment variable                  → .env
 */
import { commandText } from './doctorModel';

const TOOLCHAINS = new Set([
  'node', 'npm', 'npx', 'pnpm', 'yarn', 'bun', 'mix', 'elixir', 'erl', 'iex', 'python', 'python3', 'pip', 'pip3', 'uv',
  'poetry', 'ruby', 'bundle', 'bundler', 'rails', 'go', 'cargo', 'php', 'composer', 'docker', 'git',
]);
const NODE_RUNNERS = new Set(['npm', 'npx', 'pnpm', 'yarn', 'bun', 'node']);

const programName = (exe) =>
  String(exe || '')
    .split(/[\\/]/)
    .pop()
    .toLowerCase()
    .replace(/\.(cmd|exe|bat)$/, '');

/** The name in "sh: vite: command not found", "zsh: command not found: vite", "'vite' is not recognized…". */
export function notFoundName(text) {
  const m =
    text.match(/command not found: ([\w.@/+-]+)/) ||
    text.match(/(?:^|\n)\s*(?:[\w./-]+:\s+)?(?:(?:line )?\d+:\s+)?([\w.@/+-]+): (?:command )?not found\b/) ||
    text.match(/'([\w.@/+-]+)' is not recognized as an internal or external command/);
  return m ? m[1].split('/').pop() : null;
}

/**
 * → { cause, kind, params } | null
 *   kind: the Check setup item kind it maps to (deps | toolchain | service | db_setup | env | port)
 *   eco:  the ecosystem for deps (node | mix | python | ruby | go | php)
 */
export function diagnoseFailure({ lines = [], exitCode = null, error = null, config = {} } = {}) {
  const text = lines.slice(-80).join('\n');
  const exe = programName(config.runtimeExecutable);

  if (error && /^not_found/.test(String(error))) {
    return { cause: 'tool_missing', kind: 'toolchain', params: { tool: String(error).split(':').slice(1).join(':').trim() || exe } };
  }
  if (/Unchecked dependencies for environment|run "mix deps\.get"|the dependency is not available, run "mix deps\.get"|Could not find Hex/.test(text)) {
    return { cause: 'mix_deps', kind: 'deps', eco: 'mix', params: {} };
  }
  const nf = notFoundName(text);
  if (nf || exitCode === 127) {
    const name = nf || exe;
    if (TOOLCHAINS.has(name)) return { cause: 'tool_missing', kind: 'toolchain', params: { tool: name } };
    if (NODE_RUNNERS.has(exe)) return { cause: 'node_bin_missing', kind: 'deps', eco: 'node', params: { bin: name } };
    return { cause: 'tool_missing', kind: 'toolchain', params: { tool: name } };
  }
  let m = text.match(/Cannot find (?:module|package) '([^']+)'/) || text.match(/ERR_MODULE_NOT_FOUND[^\n]*?'([^']+)'/);
  if (m) return { cause: 'node_module_missing', kind: 'deps', eco: 'node', params: { module: m[1] } };
  m = text.match(/ModuleNotFoundError: No module named '([^']+)'/);
  if (m) return { cause: 'python_module_missing', kind: 'deps', eco: 'python', params: { module: m[1] } };
  m = text.match(/database "([^"]+)" does not exist/) || text.match(/Unknown database '([^']+)'/);
  if (m || /invalid_catalog_name|mix ecto\.create/.test(text)) {
    return { cause: 'db_missing', kind: 'db_setup', params: { database: m ? m[1] : '' } };
  }
  m =
    text.match(/(?:could not connect to server|connection refused|ECONNREFUSED|econnrefused)[^\n]*?(?:(?:localhost|127\.0\.0\.1|::1)\]?:)?(\d{4,5})/i) ||
    text.match(/tcp connect \((?:localhost|127\.0\.0\.1|[^:)]+):(\d+)\): connection refused/);
  if (m || /could not connect to server|DBConnection\.ConnectionError/.test(text)) {
    return { cause: 'db_down', kind: 'service', params: { port: m ? m[1] : '' } };
  }
  m =
    text.match(/environment variable ([A-Z][A-Z0-9_]*) is missing/i) ||
    text.match(/Missing (?:required )?environment variable:? "?([A-Z][A-Z0-9_]*)/i) ||
    text.match(/Environment variable not found: ([A-Z][A-Z0-9_]*)/) ||
    text.match(/KeyError: '([A-Z][A-Z0-9_]*)'/) ||
    text.match(/([A-Z][A-Z0-9_]+) (?:is not set|must be set)/);
  if (m) return { cause: 'env_var_missing', kind: 'env', params: { name: m[1] } };
  if (/Could not find gem|Run `bundle install`|Bundler::GemNotFound/.test(text)) return { cause: 'gems_missing', kind: 'deps', eco: 'ruby', params: {} };
  if (/missing go\.sum entry|no required module provides package/.test(text)) return { cause: 'go_mods_missing', kind: 'deps', eco: 'go', params: {} };
  if (/vendor\/autoload\.php/.test(text)) return { cause: 'vendor_missing', kind: 'deps', eco: 'php', params: {} };
  m = text.match(/EADDRINUSE[^\n]*?:(\d+)/) || text.match(/address already in use[^\n]*?:(\d+)/i);
  if (m || /EADDRINUSE|address already in use/i.test(text)) return { cause: 'port_taken', kind: 'port', params: { port: m ? m[1] : '' } };
  return null;
}

const DEPS_ID = { node: 'deps:node', mix: 'deps:mix', python: 'deps:python', ruby: 'deps:ruby', go: 'deps:go', php: 'deps:php' };
const FALLBACK = {
  mix: [{ program: 'mix', args: ['deps.get'] }],
  ruby: [{ program: 'bundle', args: ['install'] }],
  go: [{ program: 'go', args: ['mod', 'download'] }],
  php: [{ program: 'composer', args: ['install'] }],
};

const relOf = (config) =>
  String(config?.cwd || '')
    .replace(/\\/g, '/')
    .split('/')
    .filter((p) => p && p !== '.')
    .join('/');

/**
 * The Check setup item a diagnosis maps to, for this configuration: the
 * report's own item when there is one (exact command, right folder), else a
 * built-in fallback (`mix deps.get`, `npm install` …) in the config's folder.
 */
export function itemForDiagnosis(diagnosis, report, config) {
  if (!diagnosis) return null;
  const name = config?.name;
  const items = (report?.items || []).filter((i) => !name || i.configs.includes(name));
  const pick = (pred) => items.find((i) => pred(i) && i.fix) || items.find(pred);
  let found = null;
  if (diagnosis.kind === 'deps') found = pick((i) => i.id.startsWith(`${DEPS_ID[diagnosis.eco]}:`));
  else if (diagnosis.kind === 'toolchain') found = pick((i) => i.kind === 'toolchain' && i.params?.tool === diagnosis.params.tool);
  else if (diagnosis.kind === 'service') found = pick((i) => i.kind === 'service' && (!diagnosis.params.port || i.params?.port === diagnosis.params.port));
  else if (diagnosis.kind === 'db_setup') found = pick((i) => i.kind === 'db_setup');
  else if (diagnosis.kind === 'env') found = pick((i) => i.kind === 'env');
  else if (diagnosis.kind === 'port') found = pick((i) => i.kind === 'port');
  if (found) return found;
  const cwd = relOf(config);
  const fallback = (commands, kind, extra = {}) => ({
    id: `fallback:${diagnosis.cause}:${cwd}`,
    kind,
    status: 'error',
    code: diagnosis.cause,
    params: diagnosis.params,
    cwd,
    configs: name ? [name] : [],
    fix: { commands, cwd, safe: kind === 'deps', step: kind === 'deps' ? 2 : 0, ...extra },
  });
  if (diagnosis.kind === 'deps' && diagnosis.eco === 'node') {
    const pm = ['pnpm', 'yarn', 'bun'].includes(programName(config?.runtimeExecutable)) ? programName(config.runtimeExecutable) : 'npm';
    return fallback([{ program: pm, args: ['install'] }], 'deps');
  }
  if (diagnosis.kind === 'deps' && FALLBACK[diagnosis.eco]) return fallback(FALLBACK[diagnosis.eco], 'deps');
  if (diagnosis.kind === 'db_setup' && programName(config?.runtimeExecutable) === 'mix') return fallback([{ program: 'mix', args: ['ecto.create'] }], 'db_setup');
  return null;
}

export { commandText };
